import { useState } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import {
  ArrowLeft, Pencil, Trash2, CheckCircle, RotateCcw,
  User, Calendar, Flag, Plus, X, Square, CheckSquare, Repeat, Bell,
  Building2, Briefcase, UserCircle,
} from "lucide-react"
import {
  useTask, useDeleteTask, useCompleteTask, useReopenTask,
  useAddChecklistItem, useUpdateChecklistItem, useRemoveChecklistItem,
} from "@/hooks/use-tasks"
import { RecordNavigation } from "@/components/record-navigation"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { ActivityTimeline } from "@/components/activity-timeline"
import { ReminderEditor } from "@/components/reminder-editor"
import { formatDate } from "@/lib/utils"
import { toast } from "sonner"

const DAY_LABELS = ["Nd", "Pn", "Wt", "Sr", "Cz", "Pt", "So"]

function formatRecurrence(rule: { frequency: string; interval: number; days_of_week?: number[]; day_of_month?: number }): string {
  if (rule.frequency === "daily") return rule.interval === 1 ? "Codziennie" : `Co ${rule.interval} dni`
  if (rule.frequency === "weekly") {
    const days = (rule.days_of_week || []).map(d => DAY_LABELS[d]).join(", ")
    return rule.interval === 1 ? `Co tydzien: ${days}` : `Co ${rule.interval} tyg.: ${days}`
  }
  if (rule.frequency === "monthly") return rule.interval === 1 ? `Co miesiac, ${rule.day_of_month}. dnia` : `Co ${rule.interval} mies.`
  if (rule.frequency === "yearly") return rule.interval === 1 ? "Co rok" : `Co ${rule.interval} lat`
  return ""
}

const PRIORITY_COLORS: Record<string, string> = {
  low: "#6B7280",
  normal: "var(--primary)",
  high: "#F59E0B",
  urgent: "#EF4444",
}
const PRIORITY_LABELS: Record<string, string> = {
  low: "Niski",
  normal: "Normalny",
  high: "Wysoki",
  urgent: "Pilny",
}
const STATUS_LABELS: Record<string, string> = {
  open: "Otwarte",
  in_progress: "W toku",
  completed: "Ukonczone",
  cancelled: "Anulowane",
}
const STATUS_COLORS: Record<string, string> = {
  open: "var(--primary)",
  in_progress: "#F59E0B",
  completed: "#22C55E",
  cancelled: "#6B7280",
}
const ENTITY_ICONS: Record<string, typeof Building2> = {
  Company: Building2,
  Deal: Briefcase,
  Contact: UserCircle,
}

export function TaskDetailPage() {
  const { t } = useTranslation()
  const { id } = useParams()
  const navigate = useNavigate()
  const { data, isLoading } = useTask(id)
  const deleteMutation = useDeleteTask()
  const completeMutation = useCompleteTask()
  const reopenMutation = useReopenTask()
  const addChecklistMutation = useAddChecklistItem()
  const updateChecklistMutation = useUpdateChecklistItem()
  const removeChecklistMutation = useRemoveChecklistItem()

  const [newChecklistTitle, setNewChecklistTitle] = useState("")

  const task = data?.data

  const handleDelete = async () => {
    if (!id || !confirm("Czy na pewno chcesz usunac to zadanie?")) return
    await deleteMutation.mutateAsync(id)
    toast.success("Zadanie usuniete")
    navigate("/tasks")
  }

  const handleComplete = async () => {
    if (!id) return
    await completeMutation.mutateAsync(id)
    toast.success("Zadanie ukonczone")
  }

  const handleReopen = async () => {
    if (!id) return
    await reopenMutation.mutateAsync(id)
    toast.success("Zadanie otwarte ponownie")
  }

  const handleAddChecklistItem = async () => {
    if (!id || !newChecklistTitle.trim()) return
    await addChecklistMutation.mutateAsync({ taskId: id, title: newChecklistTitle.trim() })
    setNewChecklistTitle("")
  }

  const handleToggleChecklistItem = async (itemId: string, isCompleted: boolean) => {
    if (!id) return
    await updateChecklistMutation.mutateAsync({ taskId: id, itemId, data: { is_completed: !isCompleted } })
  }

  const handleRemoveChecklistItem = async (itemId: string) => {
    if (!id) return
    await removeChecklistMutation.mutateAsync({ taskId: id, itemId })
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  if (!task) {
    return <div className="text-muted-foreground">Nie znaleziono zadania</div>
  }

  const isOverdue = task.due_date && new Date(task.due_date) < new Date() && task.status !== "completed"
  const checklistProgress = task.checklist_items?.length > 0
    ? Math.round((task.checklist_items.filter(ci => ci.is_completed).length / task.checklist_items.length) * 100)
    : null

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/tasks")} className="rounded-md p-1.5 transition-colors hover:bg-muted/30 text-muted-foreground">
            <ArrowLeft size={18} />
          </button>
          <RecordNavigation entityType="tasks" currentId={id} basePath="/tasks" />
          <div>
            <div className="flex items-center gap-2">
              <h1
                className={`page-title ${task.status === "completed" ? "line-through text-muted-foreground" : ""}`}
              >
                {task.title}
              </h1>
              <Badge
                variant="outline"
                className="text-xs"
                style={{ borderColor: STATUS_COLORS[task.status], color: STATUS_COLORS[task.status] }}
              >
                {STATUS_LABELS[task.status]}
              </Badge>
              <Badge
                variant="outline"
                className="text-xs"
                style={{ borderColor: PRIORITY_COLORS[task.priority], color: PRIORITY_COLORS[task.priority] }}
              >
                {PRIORITY_LABELS[task.priority]}
              </Badge>
              {task.task_category && (
                <span
                  className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium"
                  style={{
                    backgroundColor: task.task_category.color + "18",
                    color: task.task_category.color,
                  }}
                >
                  <span
                    className="h-2 w-2 rounded-full"
                    style={{ backgroundColor: task.task_category.color }}
                  />
                  {task.task_category.name}
                </span>
              )}
            </div>
            {task.entity_links.length > 0 && (
              <div className="flex items-center gap-2 mt-0.5">
                {task.entity_links.map(link => {
                  const Icon = ENTITY_ICONS[link.entity_type] || Building2
                  return (
                    <span key={link.id} className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Icon size={12} />
                      {link.entity_name}
                    </span>
                  )
                })}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {task.status !== "completed" ? (
            <Button variant="outline" size="sm" onClick={handleComplete}>
              <CheckCircle size={14} /> Ukoncz
            </Button>
          ) : (
            <Button variant="outline" size="sm" onClick={handleReopen}>
              <RotateCcw size={14} /> Otworz ponownie
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => navigate(`/tasks/${id}/edit`)}>
            <Pencil size={14} /> {t("common.edit")}
          </Button>
          <Button variant="ghost" size="sm" onClick={handleDelete} className="text-red-600">
            <Trash2 size={14} />
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="grid grid-cols-3 gap-6">
        <div className="col-span-2">
          <Tabs defaultValue="overview">
            <TabsList>
              <TabsTrigger value="overview">Przeglad</TabsTrigger>
              <TabsTrigger value="checklist">
                Checklista{task.checklist_items?.length > 0 ? ` (${task.checklist_items.filter(ci => ci.is_completed).length}/${task.checklist_items.length})` : ""}
              </TabsTrigger>
              <TabsTrigger value="subtasks">
                Podzadania{task.subtasks?.length > 0 ? ` (${task.subtasks.length})` : ""}
              </TabsTrigger>
              <TabsTrigger value="reminders">
                Przypomnienia{task.reminders?.length > 0 ? ` (${task.reminders.length})` : ""}
              </TabsTrigger>
              <TabsTrigger value="activities">Aktywnosci</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="mt-4 space-y-4">
              {task.description && (
                <div className="surface-card p-4 space-y-2">
                  <h2 className="text-sm font-semibold text-foreground">Opis</h2>
                  <p className="text-sm whitespace-pre-wrap text-muted-foreground">{task.description}</p>
                </div>
              )}

              {/* Checklist preview */}
              {task.checklist_items?.length > 0 && (
                <div className="surface-card p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <h2 className="text-sm font-semibold text-foreground">Checklista</h2>
                    <span className="text-xs text-muted-foreground">{checklistProgress}%</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-muted">
                    <div
                      className="h-1.5 rounded-full transition-all bg-green-500"
                      style={{ width: `${checklistProgress}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Entity links */}
              {task.entity_links.length > 0 && (
                <div className="surface-card p-4 space-y-2">
                  <h2 className="text-sm font-semibold text-foreground">Powiazania</h2>
                  <div className="space-y-1.5">
                    {task.entity_links.map(link => {
                      const Icon = ENTITY_ICONS[link.entity_type] || Building2
                      return (
                        <div key={link.id} className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Icon size={14} className="text-muted-foreground" />
                          <span>{link.entity_name}</span>
                          <span className="text-xs text-muted-foreground">({link.entity_type})</span>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}
            </TabsContent>

            <TabsContent value="checklist" className="mt-4">
              <div className="surface-card">
                <div className="divide-y divide-border">
                  {task.checklist_items?.map((item) => (
                    <div key={item.id} className="flex items-center gap-3 px-4 py-2.5 group">
                      <button onClick={() => handleToggleChecklistItem(item.id, item.is_completed)} className="shrink-0">
                        {item.is_completed ? (
                          <CheckSquare size={18} className="text-green-500" />
                        ) : (
                          <Square size={18} className="text-muted-foreground" />
                        )}
                      </button>
                      <span
                        className={`flex-1 text-sm ${item.is_completed ? "line-through text-muted-foreground" : "text-foreground"}`}
                      >
                        {item.title}
                      </span>
                      <button
                        onClick={() => handleRemoveChecklistItem(item.id)}
                        className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                  {(!task.checklist_items || task.checklist_items.length === 0) && (
                    <p className="p-6 text-center text-sm text-muted-foreground">Brak elementow checklisty</p>
                  )}
                </div>
                {/* Add checklist item */}
                <div className="flex items-center gap-2 border-t px-4 py-2.5">
                  <Plus size={16} className="text-muted-foreground" />
                  <input
                    value={newChecklistTitle}
                    onChange={(e) => setNewChecklistTitle(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleAddChecklistItem()
                    }}
                    placeholder="Dodaj element..."
                    className="flex-1 bg-transparent text-sm outline-none text-foreground"
                  />
                  {newChecklistTitle.trim() && (
                    <Button size="sm" variant="ghost" onClick={handleAddChecklistItem}>
                      Dodaj
                    </Button>
                  )}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="subtasks" className="mt-4">
              <div className="surface-card">
                {(!task.subtasks || task.subtasks.length === 0) ? (
                  <p className="p-8 text-center text-sm text-muted-foreground">Brak podzadan</p>
                ) : (
                  <div className="divide-y divide-border">
                    {task.subtasks.map((sub) => (
                      <div
                        key={sub.id}
                        onClick={() => navigate(`/tasks/${sub.id}`)}
                        className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-muted/30 transition-colors"
                      >
                        {sub.status === "completed" ? (
                          <CheckCircle size={16} className="text-green-500" />
                        ) : (
                          <div className="h-4 w-4 rounded-full border border-muted-foreground" />
                        )}
                        <div className="flex-1">
                          <span
                            className={`text-sm font-medium ${sub.status === "completed" ? "line-through text-muted-foreground" : "text-foreground"}`}
                          >
                            {sub.title}
                          </span>
                        </div>
                        <Badge
                          variant="outline"
                          className="text-xs"
                          style={{ borderColor: PRIORITY_COLORS[sub.priority], color: PRIORITY_COLORS[sub.priority] }}
                        >
                          {PRIORITY_LABELS[sub.priority]}
                        </Badge>
                        {sub.due_date && (
                          <span className="text-xs text-muted-foreground">
                            {formatDate(sub.due_date)}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="reminders" className="mt-4">
              <div className="surface-card p-4">
                <ReminderEditor
                  taskId={task.id}
                  dueDate={task.due_date}
                  dueTime={task.due_time}
                  reminders={task.reminders ?? []}
                />
              </div>
            </TabsContent>

            <TabsContent value="activities" className="mt-4">
              <ActivityTimeline entityType="Task" entityId={task.id} />
            </TabsContent>
          </Tabs>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <div className="surface-card p-4 space-y-3">
            <div>
              <span className="text-xs text-muted-foreground">{t("tasks.status")}</span>
              <div className="flex items-center gap-2 mt-0.5">
                <div className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: STATUS_COLORS[task.status] }} />
                <p className="text-sm text-foreground">{STATUS_LABELS[task.status]}</p>
              </div>
            </div>
            <div>
              <span className="text-xs text-muted-foreground">{t("tasks.priority")}</span>
              <div className="flex items-center gap-2 mt-0.5">
                <Flag size={14} style={{ color: PRIORITY_COLORS[task.priority] }} />
                <p className="text-sm text-foreground">{PRIORITY_LABELS[task.priority]}</p>
              </div>
            </div>
            {task.task_category && (
              <div>
                <span className="text-xs text-muted-foreground">Kategoria</span>
                <div className="flex items-center gap-2 mt-0.5">
                  <span
                    className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium"
                    style={{
                      backgroundColor: task.task_category.color + "18",
                      color: task.task_category.color,
                    }}
                  >
                    <span
                      className="h-2 w-2 rounded-full"
                      style={{ backgroundColor: task.task_category.color }}
                    />
                    {task.task_category.name}
                  </span>
                </div>
              </div>
            )}
            {task.assignee && (
              <div>
                <span className="text-xs text-muted-foreground">{t("tasks.assignee")}</span>
                <div className="flex items-center gap-2 mt-0.5">
                  <User size={14} className="text-muted-foreground" />
                  <p className="text-sm text-foreground">{task.assignee.first_name} {task.assignee.last_name}</p>
                </div>
              </div>
            )}
            {task.due_date && (
              <div>
                <span className="text-xs text-muted-foreground">{t("tasks.dueDate")}</span>
                <div className="flex items-center gap-2 mt-0.5">
                  <Calendar size={14} className={isOverdue ? "text-red-500" : "text-muted-foreground"} />
                  <p className={`text-sm ${isOverdue ? "text-red-500" : ""}`}>
                    {formatDate(task.due_date)}
                    {task.due_time && <span className="ml-1">{task.due_time}</span>}
                  </p>
                </div>
              </div>
            )}
            {task.task_type && (
              <div>
                <span className="text-xs text-muted-foreground">Typ</span>
                <p className="text-sm text-foreground">{task.task_type}</p>
              </div>
            )}
            {task.recurrence_rule && (
              <div>
                <span className="text-xs text-muted-foreground">Powtarzalnosc</span>
                <div className="flex items-center gap-2 mt-0.5">
                  <Repeat size={14} className="text-primary" />
                  <p className="text-sm text-foreground">
                    {formatRecurrence(task.recurrence_rule)}
                  </p>
                </div>
                {task.recurrence_end_date && (
                  <p className="text-xs mt-0.5 text-muted-foreground">
                    Do: {formatDate(task.recurrence_end_date)}
                  </p>
                )}
              </div>
            )}
            {task.reminders && task.reminders.length > 0 && (
              <div>
                <span className="text-xs text-muted-foreground">Przypomnienia</span>
                <div className="flex items-center gap-2 mt-0.5">
                  <Bell size={14} className="text-primary" />
                  <p className="text-sm text-foreground">
                    {task.reminders.filter(r => !r.is_sent).length} oczekujacych
                  </p>
                </div>
              </div>
            )}
            {task.completed_at && (
              <div>
                <span className="text-xs text-muted-foreground">Ukonczone</span>
                <p className="text-sm text-foreground">
                  {formatDate(task.completed_at)}
                  {task.completed_by && ` \u00B7 ${task.completed_by.first_name} ${task.completed_by.last_name}`}
                </p>
              </div>
            )}
            {task.creator && (
              <div className="border-t pt-3">
                <span className="text-xs text-muted-foreground">Utworzyl</span>
                <p className="text-sm text-foreground">{task.creator.first_name} {task.creator.last_name}</p>
              </div>
            )}
            <div className="border-t pt-3">
              <span className="text-xs text-muted-foreground">Utworzono</span>
              <p className="text-sm text-foreground">{formatDate(task.created_at)}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
